import useNotifyForm from '@/hooks/ProductFullDetail/NotifyForm/useNotifyForm';
import { validateEmail } from '@/util/formValidators';
import { ErrorMessage } from '@hookform/error-message';
import clsx from 'clsx';
import Link from 'next/link';
import { useForm } from 'react-hook-form';
import FormError from '../../FormError';

const NotifyForm = props => {
  const {
    product_id,
    configurable_options,
    selectedOptionsArray,
    variants,
    onClose,
    productDetailsName,
    productSku,
    isSubmitOk,
    setIsSubmitOk,
  } = props;
  const {
    register,
    handleSubmit: handleSubmitForm,
    formState,
  } = useForm({
    defaultValues: {
      email: '',
      agree: true,
    },
  });

  const { isLoading, errors, handleSubmit } = useNotifyForm({
    configurable_options,
    selectedOptionsArray,
    variants,
    product_id,
    setIsSubmitOk,
    productSku,
  });

  const getSelectedOption = attributeCode => {
    if (!Array.isArray(configurable_options)) return null;

    const options = configurable_options.find(item => item.attribute_code === attributeCode);

    return options && options.values.find(temp => selectedOptionsArray.some(option => temp.uid === option));
  };

  return (
    <div>
      <div className='flex min-h-[50px] items-center justify-between border-b-1 border-solid border-e0e0e0 px-3 lg_min-h-[60px] lg_px-5'>
        <p className='text-2xl leading-none'>NOTIFY ME</p>
        <button
          type='button'
          onClick={onClose}
        >
          <svg
            xmlns='http://www.w3.org/2000/svg'
            width='15'
            height='16'
            fill='none'
            viewBox='0 0 15 16'
          >
            <path
              fill='#000'
              d='M13.454 15.354 7.5 9.401l-5.954 5.953a.497.497 0 0 1-.7 0l-.701-.7a.497.497 0 0 1 0-.7L6.1 8 .145 2.046a.497.497 0 0 1 0-.7l.7-.701a.497.497 0 0 1 .701 0L7.5 6.599 13.454.645a.497.497 0 0 1 .7 0l.702.7a.496.496 0 0 1 0 .701L8.9 8l5.954 5.954a.496.496 0 0 1 0 .7l-.701.701a.494.494 0 0 1-.7 0'
            ></path>
          </svg>
        </button>
      </div>

      {isSubmitOk ? (
        <div className='px-3 py-5 lg_px-[35px] lg_pt-[17px]'>
          <p className='text-center font-teko-medium text-[48px]'>Thank You!</p>
          <p className='mt-[5px] text-center font-roboto-medium text-base'>
            We&apos;ll give you a shout as soon as the
          </p>
          <p className='mt-[5px] text-center font-teko-medium text-base'>{productDetailsName}</p>
          <p className='mt-[5px] text-center font-roboto-medium text-base'>is in stock.</p>
          <button
            onClick={() => onClose()}
            type='button'
            className='mx-auto mt-5 block h-10 w-[200px] rounded-[102px] bg-black font-roboto-medium text-white lg_h-[50px]'
          >
            Continue Shopping
          </button>
        </div>
      ) : (
        <>
          <FormError
            allowErrorMessages
            errors={Array.from(errors.values())}
          />
          <form
            className='px-3 py-5 lg_px-[35px] lg_pt-[17px]'
            onSubmit={handleSubmitForm(handleSubmit)}
          >
            {Array.isArray(configurable_options) && configurable_options.length > 0 ? (
              <div className='grid grid-cols-1 gap-5 lg_grid-cols-2'>
                {configurable_options.map(option => {
                  const selectedOption = getSelectedOption(option.attribute_code);

                  if (!selectedOption) {
                    return null;
                  }

                  const label = option.label;
                  const id = `notify-${option.attribute_code}`;

                  return (
                    <div key={id}>
                      <div>
                        <span className='font-roboto-medium text-base font-medium leading-none text-[#b23c3c]'>*</span>
                        <span className='ml-[3px] font-roboto-medium text-base font-medium leading-6'>{label}</span>
                      </div>
                      <input
                        className='mt-2.5 h-[50px] w-full border-none bg-f9f9f9 px-2.5 font-roboto-medium text-sm text-[#929292] outline-0'
                        value={selectedOption.label}
                        readOnly
                      />
                    </div>
                  );
                })}
              </div>
            ) : null}
            <div className='mt-5'>
              <div>
                <span className='font-roboto-medium text-base font-medium leading-none text-[#b23c3c]'>*</span>
                <span className='ml-[3px] font-roboto-medium text-base font-medium leading-6'>Email</span>
              </div>
              <input
                className='mt-2.5 h-[50px] w-full border-none bg-f9f9f9 px-2.5 font-roboto-medium text-sm text-black outline-0'
                {...register('email', { validate: validateEmail })}
              />
              <ErrorMessage
                name='email'
                errors={formState.errors}
                render={({ message }) => {
                  return (
                    <div className='relative'>
                      <span className='absolute font-roboto-light text-xs text-b23c3c'>{message}</span>
                    </div>
                  );
                }}
              />
            </div>
            <p className='mt-5 font-roboto-light text-sm font-light leading-5'>
              By clicking submit, you will be granting HISEA consent to send you a one-time email notifying you of the
              availability of this product.
            </p>
            <div className='mt-[30px] text-center lg_mt-10'>
              <button
                type='submit'
                disabled={isLoading}
                className='h-10 w-full rounded-[102px] bg-black font-roboto-medium text-base font-medium text-white lg_h-[50px] lg_max-w-[120px] lg_text-lg lg_hover_bg-4c4c4c'
              >
                Submit
              </button>
            </div>
            <label className='mt-5 flex items-start gap-x-2.5'>
              <div className='relative translate-y-[3px] text-[0]'>
                <input
                  {...register('agree')}
                  type='checkbox'
                  className={clsx(
                    'peer h-[18px] w-[18px] flex-none  appearance-none rounded-[2px] border-1 border-solid border-[#4C4C4C] checked_bg-black',
                  )}
                />
                <div className='absolute left-1/2 top-1/2 hidden h-full w-full -translate-x-1/2 -translate-y-1/2 items-center justify-center peer-checked_flex '>
                  <svg
                    xmlns='http://www.w3.org/2000/svg'
                    width='14'
                    height='10'
                    fill='none'
                    viewBox='0 0 14 10'
                  >
                    <path
                      fill='#fff'
                      d='M13.296.668A.94.94 0 0 0 11.94.65L5.41 7.206 2.037 3.74c-.35-.36-.937-.34-1.312.045-.374.384-.394.988-.043 1.349L4.805 9.37c.35.36.937.34 1.312-.045q.127-.133.198-.294.056-.039.107-.088l6.858-6.883c.378-.38.385-1.004.016-1.393'
                    ></path>
                  </svg>
                </div>
              </div>
              <div className='font-roboto-light text-sm'>
                I am interested in receiving email messages to this account from hisea regarding product updates,
                marketing notifications and other hisea news. You can opt-out at any time. For more information, visit
                our{' '}
                <Link
                  className='font-roboto-medium underline'
                  href='/privacy-policy'
                >
                  Privacy Policy
                </Link>
                .
              </div>
            </label>
          </form>
        </>
      )}
    </div>
  );
};

export default NotifyForm;
